// src/utils/chatStreaming.ts
import type { User } from 'firebase/auth';
import type { Message } from '@/lib/firebase';
import type { ChatMessage as LocalChatMessage, Document } from '@/types';
import type { SelectedItem } from '@/types/file-browser';
import { vacChat } from '@/utils/vacChat';
import type { AccessControl } from '@/lib/firebase';
import type { ArtifactData } from '@/types';

export type StreamingConfig = {
  message: string;
  assistantId: string;
  templateId?: string;
  accessControl?: AccessControl;
  name: string;
  avatar: string;
  initialInstructions: string;
  initialDocuments: Document[];
  tools: string[];
  toolConfigs: Record<string, Record<string, any>>;
  currentUser: User | null;
  firestoreMessages: Message[];
  sessionId: string;
  traceId?: string;  // Add traceId for Langfuse tracking
  selectedItems: SelectedItem[];
  adminEmail?: string;
  senderName?: string;
  signal: AbortSignal;
  // Tool confirmation parameters
  confirm_past_pause?: boolean;
  tools_to_use?: import('@/types').FirstImpressionTool[];
  // Langfuse tracking
  langfuseTrace?: any;
  latencyTracker?: any;
};

export type StreamingCallbacks = {
  onStreamStart: () => void;
  onStreamChunk: (chunk: string, isFirstChunk: boolean) => void;
  onStreamEnd: (finalContent: string) => void;
  onStreamError: (error: unknown) => void;
  // New callback for thinking content
  onThinkingContent?: (thinkingContent: string) => void;
  // New callback for artifact content
  onArtifactContent?: (artifacts: ArtifactData[]) => void;
};

/**
 * Track and manage thinking content tags across chunks
 */
class ThinkingContentTracker {
  private buffer: string = '';
  private isInsideThinking: boolean = false;
  private fullThinkingContent: string = '';
  private lastSentThinkingLength: number = 0;
  
  /**
   * Process a chunk and extract any thinking content
   * @returns An object with both the clean content (without thinking tags) and any extracted thinking content
   */
  processChunk(chunk: string): { cleanContent: string, thinkingContent: string | null, incrementalThinking: string | null } {
    // Add chunk to buffer
    this.buffer += chunk;
    
    // Temporary working copy of buffer
    let remaining = this.buffer;
    let cleanedContent = '';
    let extractedThinking = '';
    let thinkingChanged = false;
    
    // Process all complete thinking tags
    while (true) {
      if (this.isInsideThinking) {
        // Look for closing tag
        const closingIndex = remaining.indexOf('</thinking>');
        if (closingIndex === -1) {
          // No closing tag yet, keep everything in buffer
          break;
        }
        
        // Extract thinking content
        const thinkingContent = remaining.substring(0, closingIndex);
        extractedThinking += thinkingContent;
        this.fullThinkingContent += thinkingContent;
        thinkingChanged = true;
        
        // Move past closing tag
        remaining = remaining.substring(closingIndex + 11); // 11 is length of '</thinking>'
        this.isInsideThinking = false;
      } else {
        // Look for opening tag
        const openingIndex = remaining.indexOf('<thinking>');
        if (openingIndex === -1) {
          // No more thinking tags, add everything to cleaned content
          cleanedContent += remaining;
          remaining = '';
          break;
        }
        
        // Add content before thinking tag to cleaned content
        cleanedContent += remaining.substring(0, openingIndex);
        
        // Move past opening tag
        remaining = remaining.substring(openingIndex + 10); // 10 is length of '<thinking>'
        this.isInsideThinking = true;
      }
    }
    
    // Update buffer with any remaining unprocessed content
    this.buffer = remaining;
    
    // Handle incremental thinking content
    let incrementalThinking = null;
    if (thinkingChanged || (this.isInsideThinking && remaining.length > 0)) {
      // Calculate current thinking content length including any unprocessed buffer if we're inside a thinking tag
      const currentThinkingContent = this.fullThinkingContent + (this.isInsideThinking ? remaining : '');
      
      if (currentThinkingContent.length > this.lastSentThinkingLength) {
        // Only send the new portion of thinking content
        incrementalThinking = currentThinkingContent.substring(this.lastSentThinkingLength);
        
        // Update the last sent position if we've finished a thinking tag
        if (!this.isInsideThinking) {
          this.lastSentThinkingLength = this.fullThinkingContent.length;
        }
      }
    }
    
    return {
      cleanContent: cleanedContent,
      thinkingContent: thinkingChanged ? this.fullThinkingContent : null,
      incrementalThinking: incrementalThinking
    };
  }
  
  /**
   * Get any leftover thinking content from incomplete tags and clear buffer
   */
  finalize(): { cleanContent: string, thinkingContent: string | null, incrementalThinking: string | null } {
    let cleanedContent = '';
    let thinkingChanged = false;
    let incrementalThinking = null;
    
    if (this.isInsideThinking) {
      // We have an unclosed thinking tag at the end - consider it part of thinking content
      this.fullThinkingContent += this.buffer;
      thinkingChanged = true;
      
      // Check if we have new thinking content to send incrementally
      if (this.fullThinkingContent.length > this.lastSentThinkingLength) {
        incrementalThinking = this.fullThinkingContent.substring(this.lastSentThinkingLength);
        this.lastSentThinkingLength = this.fullThinkingContent.length;
      }
    } else {
      // No unclosed thinking tag, remaining buffer is clean content
      cleanedContent = this.buffer;
    }
    
    // Clear buffer
    this.buffer = '';
    this.isInsideThinking = false;
    
    return {
      cleanContent: cleanedContent,
      thinkingContent: thinkingChanged ? this.fullThinkingContent : null,
      incrementalThinking: incrementalThinking
    };
  }
}

/**
 * Track and manage artifact content tags across chunks
 */
class ArtifactContentTracker {
  private buffer: string = '';
  private isInsideArtifact: boolean = false;
  private currentArtifactInfo: { id: string; title: string; type: string } | null = null;
  private currentArtifactContent: string = '';
  private extractedArtifacts: ArtifactData[] = [];
  
  /**
   * Process a chunk and extract any artifact content
   * @returns An object with clean content and any completed artifacts
   */
  processChunk(chunk: string): { cleanContent: string, newArtifacts: ArtifactData[] } {
    // Add chunk to buffer
    this.buffer += chunk;
    
    // Temporary working copy of buffer
    let remaining = this.buffer;
    let cleanedContent = '';
    const newArtifacts: ArtifactData[] = [];
    
    // Process all complete artifact tags
    while (true) {
      if (this.isInsideArtifact) {
        // Look for closing tag
        const closingIndex = remaining.indexOf('</artifact>');
        if (closingIndex === -1) {
          // No closing tag yet, accumulate content
          this.currentArtifactContent += remaining;
          remaining = '';
          break;
        }
        
        // Extract artifact content
        const artifactContent = remaining.substring(0, closingIndex);
        this.currentArtifactContent += artifactContent;
        
        // Create complete artifact
        if (this.currentArtifactInfo) {
          const artifact: ArtifactData = {
            ...this.currentArtifactInfo,
            content: this.currentArtifactContent.trim()
          };
          newArtifacts.push(artifact);
          this.extractedArtifacts.push(artifact);
        }
        
        // Reset state
        this.isInsideArtifact = false;
        this.currentArtifactInfo = null;
        this.currentArtifactContent = '';
        
        // Move past closing tag
        remaining = remaining.substring(closingIndex + 11); // 11 is length of '</artifact>'
      } else {
        // Look for opening artifact tag
        const artifactMatch = remaining.match(/<artifact\s+id=["']([^"']+)["']\s+title=["']([^"']+)["']\s+type=["']([^"']+)["']>/);
        if (!artifactMatch) {
          // No more artifact tags, add everything to cleaned content
          cleanedContent += remaining;
          remaining = '';
          break;
        }
        
        const fullMatch = artifactMatch[0];
        const matchIndex = remaining.indexOf(fullMatch);
        
        // Add content before artifact tag to cleaned content
        cleanedContent += remaining.substring(0, matchIndex);
        
        // Extract artifact info
        this.currentArtifactInfo = {
          id: artifactMatch[1].trim(),
          title: artifactMatch[2].trim(),
          type: artifactMatch[3].trim()
        };
        
        // Move past opening tag
        remaining = remaining.substring(matchIndex + fullMatch.length);
        this.isInsideArtifact = true;
        this.currentArtifactContent = '';
      }
    }
    
    // Update buffer with any remaining unprocessed content
    this.buffer = remaining;
    
    return {
      cleanContent: cleanedContent,
      newArtifacts: newArtifacts
    };
  }
  
  /**
   * Get all artifacts extracted so far
   */
  getAllArtifacts(): ArtifactData[] {
    return [...this.extractedArtifacts];
  }
  
  /**
   * Finalize and extract any remaining artifact content
   */
  finalize(): { cleanContent: string, finalArtifacts: ArtifactData[] } {
    let cleanedContent = '';
    const finalArtifacts: ArtifactData[] = [];
    
    if (this.isInsideArtifact && this.currentArtifactInfo) {
      // We have an unclosed artifact tag at the end - consider it complete
      this.currentArtifactContent += this.buffer;
      const artifact: ArtifactData = {
        ...this.currentArtifactInfo,
        content: this.currentArtifactContent.trim()
      };
      finalArtifacts.push(artifact);
      this.extractedArtifacts.push(artifact);
    } else {
      // No unclosed artifact tag, remaining buffer is clean content
      cleanedContent = this.buffer;
    }
    
    // Clear state
    this.buffer = '';
    this.isInsideArtifact = false;
    this.currentArtifactInfo = null;
    this.currentArtifactContent = '';
    
    return {
      cleanContent: cleanedContent,
      finalArtifacts: finalArtifacts
    };
  }
}

/**
 * Handles message streaming from the chat API with complete separation of thinking content
 */
export async function handleMessageStreaming(
  config: StreamingConfig,
  callbacks: StreamingCallbacks
): Promise<void> {
  
  const {
    message,
    assistantId,
    templateId,
    accessControl,
    name,
    avatar,
    initialInstructions,
    initialDocuments,
    tools,
    toolConfigs,
    currentUser,
    firestoreMessages,
    sessionId,
    traceId,
    selectedItems,
    adminEmail,
    senderName,
    signal,
    confirm_past_pause,
    tools_to_use
  } = config;

  const {
    onStreamStart,
    onStreamChunk,
    onStreamEnd,
    onStreamError,
    onThinkingContent,
    onArtifactContent
  } = callbacks;

  // Start streaming
  onStreamStart();
  
  // Track complete content for Firestore storage (including thinking content)
  let fullContentRef = '';
  // Track only the visible content (without thinking tags)
  let visibleContentRef = '';
  
  // Create trackers to manage thinking and artifact content across chunks
  const thinkingTracker = new ThinkingContentTracker();
  const artifactTracker = new ArtifactContentTracker();
  
  try {
    // Prepare chat history
    const chatHistoryForApi = firestoreMessages
      .filter(msg => msg.sender === 'user' || msg.sender === 'assistant')
      .map(msg => ({
        name: msg.sender === 'user' ? 'Human' : 'AI',
        content: msg.content,
      }));

    // Flag to track if we've received any content
    let hasReceivedContent = false;
    
    const emissaryConfigForApi = {
      assistantId,
      templateId, 
      accessControl,
      name,
      avatar,
      initialInstructions,
      initialDocuments,
      tools,
      toolConfigs,
      // User info with session ID
      currentUser: currentUser ? {
        email: currentUser.email,
        displayName: currentUser.displayName,
        uid: currentUser.uid,
        photoURL: currentUser.photoURL,
        sessionId,
      } : undefined,
      selectedItems,
      adminEmail,
      senderName,
      // Tool confirmation parameters (only include if defined)
      ...(confirm_past_pause !== undefined && { confirm_past_pause }),
      ...(tools_to_use && { tools_to_use }),
    };

    await vacChat({
      userMessage: message,
      chatHistory: chatHistoryForApi,
      langfuseTrace: config.langfuseTrace,
      latencyTracker: config.latencyTracker,
      onBotMessage: (response) => {
        // Get new content chunk
        const chunk = response.content;
        
        // Store the complete raw content for Firestore saving
        fullContentRef += chunk;
        
        // Process chunk to separate thinking content
        const { cleanContent: thinkingCleanContent, thinkingContent, incrementalThinking } = thinkingTracker.processChunk(chunk);
        
        // Process the thinking-clean content to separate artifacts
        const { cleanContent: finalCleanContent, newArtifacts } = artifactTracker.processChunk(thinkingCleanContent);
        
        // Only send final clean content to the streaming UI
        if (finalCleanContent) {
          visibleContentRef += finalCleanContent;
          onStreamChunk(finalCleanContent, !hasReceivedContent);
          hasReceivedContent = true;
        }
        
        // Send incremental thinking content if available
        if (incrementalThinking && onThinkingContent) {
          onThinkingContent(incrementalThinking);
        }
        
        // Send new artifacts if available
        if (newArtifacts.length > 0 && onArtifactContent) {
          onArtifactContent(newArtifacts);
        }
      },
      apiEndpoint: `/vac/assistant/${assistantId}/sse`, // Use SSE streaming endpoint for real-time streaming with assistant config
      instructions: initialInstructions,
      documents: initialDocuments,
      emissaryConfig: emissaryConfigForApi,
      save_to_history: true,  // Move save_to_history to root level where backend expects it
      user_message_already_saved: true,  // Frontend already saved user message
      enable_email_notifications: true,  // Can be made configurable for performance
      userId: currentUser?.uid,
      assistantId: assistantId,
      traceId: traceId,  // Pass the existing trace ID
      signal: signal 
    });

    // Process any remaining buffer content
    const { cleanContent: thinkingCleanContent, thinkingContent, incrementalThinking } = thinkingTracker.finalize();
    const { cleanContent: finalCleanContent, finalArtifacts } = artifactTracker.finalize();
    
    // Process any final thinking-clean content through artifact tracker
    let combinedFinalContent = finalCleanContent;
    if (thinkingCleanContent) {
      const { cleanContent: additionalCleanContent, newArtifacts: additionalArtifacts } = artifactTracker.processChunk(thinkingCleanContent);
      combinedFinalContent += additionalCleanContent;
      finalArtifacts.push(...additionalArtifacts);
    }
    
    // Send any final clean content
    if (combinedFinalContent) {
      visibleContentRef += combinedFinalContent;
      onStreamChunk(combinedFinalContent, !hasReceivedContent);
    }
    
    // Send any final incremental thinking content
    if (incrementalThinking && onThinkingContent) {
      onThinkingContent(incrementalThinking);
    }
    
    // Send any final artifacts
    if (finalArtifacts.length > 0 && onArtifactContent) {
      onArtifactContent(finalArtifacts);
    }

    // Signal end of stream with complete message
    console.log('Calling onStreamEnd with final content length:', visibleContentRef.length);
    onStreamEnd(visibleContentRef);

    // NOTE: Message saving is now handled by the backend via /vac/assistant/<assistantId>
    // The proxy route automatically routes to the assistant endpoint which saves messages
    // Frontend no longer saves assistant messages to avoid duplicates
    if (!signal.aborted && fullContentRef && assistantId && currentUser?.email) {
        console.log('Assistant message will be saved by backend with separated thinking content and artifacts');
    }
  } catch (err: unknown) {
    // Handle errors
    if (err instanceof DOMException && err.name === 'AbortError') {
      const wasTimeout = fullContentRef === '';
      
      if (wasTimeout) {
        console.log('Stream aborted due to timeout');
        onStreamError(new Error('The response took too long. Please try again.'));
      } else {
        console.log('Stream aborted by user action');
        
        if (fullContentRef && assistantId && currentUser?.email) {
            // NOTE: Partial assistant message saving is handled by backend
            console.log('Partial assistant message will be handled by backend with separated thinking content and artifacts');
        }
        
        onStreamEnd(visibleContentRef + ' [interrupted]');
      }
    } else {
      console.error('Error in chat streaming:', err);
      onStreamError(err);
    }
  }
}